(function (Drupal, once) {
  'use strict';

  // Create or extend shared namespace
  window.McmnExperience = window.McmnExperience || {};
  const Mcmn = window.McmnExperience;

  // State management for each chapter
  const chapterStates = {};
  
  // Global audio management - track all audio instances
  const audioInstances = {};
  
  // Track active chapter instances for cleanup
  const activeChapterInstances = {};

  /**
   * Cookie Utility Functions for MCMN Experience Tracking
   * 
   * This system tracks user progress through the MCMN Experience and stores
   * completion status in a cookie that persists for 1 year.
   * 
   * ========================================
   * GATED CONTENT ACCESS REQUIREMENTS:
   * ========================================
   * CURRENT: Newsletter submission ONLY
   * DISABLED: Experience completion requirement (can be re-enabled)
   * 
   * To re-enable experience completion requirement:
   * 1. Update hasGatedAccess() function (line ~280)
   * 2. Update updateGatedContentVisibility() function (line ~1965)
   * 3. Update newsletter tracking logic (lines ~1789, ~1870)
   * 
   * USAGE FOR GATED CONTENT:
   * 
   * METHOD 1: CSS Body Class (Simplest - Recommended)
   * When newsletter is submitted, body gets class 'mcmn-gated-access'
   * 
   * CSS:
   * .gated-content { display: none; }
   * body.mcmn-gated-access .gated-content { display: block; }
   * 
   * METHOD 2: JavaScript Check
   * if (window.McmnExperience && window.McmnExperience.CookieManager) {
   *   const hasAccess = window.McmnExperience.CookieManager.hasGatedAccess();
   *   if (hasAccess) {
   *     document.getElementById('gated-content').style.display = 'block';
   *   } else {
   *     document.getElementById('newsletter-prompt').style.display = 'block';
   *   }
   * }
   * 
   * METHOD 3: Server-side (PHP/Twig)
   * {% set mcmn_progress = cookie('mcmn_experience_progress') %}
   * {% if mcmn_progress %}
   *   {% set progress_data = mcmn_progress|json_decode %}
   *   {% if progress_data.newsletterSubmitted %}
   *     {# Show gated content #}
   *   {% endif %}
   * {% endif %}
   * 
   * Reset progress (for testing):
   * window.McmnExperience.CookieManager.resetProgress();
   * 
   * Cookie structure:
   * {
   *   started: boolean,
   *   completed: boolean,
   *   viewedSections: array,
   *   completedDate: ISO date string,
   *   newsletterSubmitted: boolean,
   *   newsletterSubmittedDate: ISO date string,
   *   version: string
   * }
   */
  const CookieManager = {
    // Cookie name for MCMN experience tracking
    cookieName: 'mcmn_experience_progress',
    
    // Cookie expiry: 1 year
    expiryDays: 365,
    
    /**
     * Set a cookie
     */
    setCookie: function(name, value, days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = 'expires=' + date.toUTCString();
      document.cookie = name + '=' + value + ';' + expires + ';path=/;SameSite=Lax';
    },
    
    /**
     * Get a cookie
     */
    getCookie: function(name) {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    },
    
    /**
     * Delete a cookie
     */
    deleteCookie: function(name) {
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/';
    },
    
    /**
     * Get MCMN progress data
     */
    getProgress: function() {
      const cookie = this.getCookie(this.cookieName);
      if (cookie) {
        try {
          return JSON.parse(decodeURIComponent(cookie));
        } catch (e) {
          return this.getDefaultProgress();
        }
      }
      return this.getDefaultProgress();
    },
    
    /**
     * Default progress structure
     */
    getDefaultProgress: function() {
      return {
        started: false,
        completed: false,
        viewedSections: [],
        completedHotspots: {
          intro: [],
          chapter1: [],
          chapter2: [],
          chapter3: [],
          chapter4: []
        },
        completedDate: null,
        newsletterSubmitted: false,
        newsletterSubmittedDate: null,
        version: '1.0'
      };
    },
    
    /**
     * Save MCMN progress data
     */
    saveProgress: function(progressData) {
      const jsonString = JSON.stringify(progressData);
      this.setCookie(this.cookieName, encodeURIComponent(jsonString), this.expiryDays);
    },
    
    /**
     * Mark experience as started
     */
    markStarted: function() {
      const progress = this.getProgress();
      progress.started = true;
      this.saveProgress(progress);
    },
    
    /**
     * Add a viewed section
     */
    addViewedSection: function(sectionName) {
      const progress = this.getProgress();
      if (!progress.viewedSections.includes(sectionName)) {
        progress.viewedSections.push(sectionName);
        this.saveProgress(progress);
      }
      return progress.viewedSections;
    },
    
    /**
     * Mark experience as completed
     */
    markCompleted: function() {
      const progress = this.getProgress();
      progress.completed = true;
      progress.completedDate = new Date().toISOString();
      this.saveProgress(progress);
    },
    
    /**
     * Check if user has completed the experience
     */
    hasCompleted: function() {
      const progress = this.getProgress();
      return progress.completed === true;
    },
    
    /**
     * Check if user has started the experience
     */
    hasStarted: function() {
      const progress = this.getProgress();
      return progress.started === true;
    },
    
    /**
     * Get list of viewed sections
     */
    getViewedSections: function() {
      const progress = this.getProgress();
      return progress.viewedSections || [];
    },
    
    /**
     * Mark a hotspot as completed for a section
     */
    markHotspotComplete: function(sectionName, hotspotId) {
      const progress = this.getProgress();
      if (!progress.completedHotspots) {
        progress.completedHotspots = {
          intro: [],
          chapter1: [],
          chapter2: [],
          chapter3: [],
          chapter4: []
        };
      }
      if (!progress.completedHotspots[sectionName]) {
        progress.completedHotspots[sectionName] = [];
      }
      if (!progress.completedHotspots[sectionName].includes(hotspotId)) {
        progress.completedHotspots[sectionName].push(hotspotId);
        this.saveProgress(progress);
      }
      return progress.completedHotspots[sectionName];
    },
    
    /**
     * Get completed hotspots for a section
     */
    getCompletedHotspots: function(sectionName) {
      const progress = this.getProgress();
      if (!progress.completedHotspots || !progress.completedHotspots[sectionName]) {
        return [];
      }
      return progress.completedHotspots[sectionName] || [];
    },
    
    /**
     * Check if all required hotspots for a section are completed
     */
    areAllHotspotsComplete: function(sectionName) {
      const requiredHotspots = {
        intro: ['hotspot'],
        chapter1: ['ornament-grab', 'lights-hotspot'], // Complete when grabs ornament AND turns on lights
        chapter2: ['hotspot'],
        chapter3: ['food-grab'], // Complete when grabs 1 piece of food
        chapter4: ['grass-grab'] // Complete when grabs 1 piece of grass
      };
      
      const required = requiredHotspots[sectionName] || [];
      if (required.length === 0) {
        return true; // No hotspots required
      }
      
      const completed = this.getCompletedHotspots(sectionName);
      return required.every(function(hotspotId) {
        return completed.includes(hotspotId);
      });
    },
    
    /**
     * Mark newsletter as submitted
     */
    markNewsletterSubmitted: function() {
      const progress = this.getProgress();
      progress.newsletterSubmitted = true;
      progress.newsletterSubmittedDate = new Date().toISOString();
      this.saveProgress(progress);
    },
    
    /**
     * Check if user has submitted newsletter
     */
    hasSubmittedNewsletter: function() {
      const progress = this.getProgress();
      return progress.newsletterSubmitted === true;
    },
    
  /**
   * Check if user has access to gated content (submitted newsletter only)
   * NOTE: Experience completion requirement temporarily disabled
   * To re-enable: return progress.completed === true && progress.newsletterSubmitted === true;
   */
  hasGatedAccess: function() {
    const progress = this.getProgress();
    // return progress.completed === true && progress.newsletterSubmitted === true; // ORIGINAL - both required
    return progress.newsletterSubmitted === true; // CURRENT - newsletter only
  },
    
    /**
     * Reset progress (for testing/debugging)
     */
    resetProgress: function() {
      this.deleteCookie(this.cookieName);
    }
  };
  
  // Export cookie manager to global namespace for access
  Mcmn.CookieManager = CookieManager;
  
  // Export checkCompletion function for use in chapter files
  Mcmn.checkCompletion = checkCompletion;

  /**
   * Get or create state for a chapter
   */
  function getChapterState(sectionType) {
    if (!chapterStates[sectionType]) {
      if (sectionType === 'intro') {
        chapterStates[sectionType] = {
          lightsOn: false,
          isPlaying: false, // Audio playback state
          isMuted: false // Audio mute state
        };
      } else if (sectionType === 'chapter3') {
        chapterStates[sectionType] = {
          placedFoodItems: new Set(), // Track which food items are on the plate
          foodPositions: {}, // Store positions: { foodId: { x, y } }
          isPlaying: false, // Audio playback state
          isMuted: false // Audio mute state
        };
      } else if (sectionType === 'chapter4') {
        chapterStates[sectionType] = {
          placedGrass: new Set(), // Track which grass elements are in the box
          grassPositions: {}, // Store positions: { grassIndex: { x, y } }
          isPlaying: false, // Audio playback state
          isMuted: false // Audio mute state
        };
      } else {
        chapterStates[sectionType] = {
          lightsOn: false,
          placedOrnaments: new Set(),
          ornamentPositions: {} // Store positions: { ornamentIndex: { x, y } }
        };
      }
    }
    return chapterStates[sectionType];
  }

  /**
   * Save state for a chapter
   */
  function saveChapterState(sectionType, state) {
    chapterStates[sectionType] = state;
  }

  /**
   * Register audio instance for a section
   * Stops any existing audio for this section first to ensure only one plays at a time
   */
  function registerAudio(sectionType, audio) {
    // Stop any existing audio for this section first
    if (audioInstances[sectionType]) {
      const existingAudio = audioInstances[sectionType];
      if (existingAudio && !existingAudio.paused) {
        existingAudio.pause();
        existingAudio.currentTime = 0;
      }
    }
    audioInstances[sectionType] = audio;
  }

  /**
   * Stop all audio instances
   */
  function stopAllAudio() {
    Object.keys(audioInstances).forEach(function(sectionType) {
      const audio = audioInstances[sectionType];
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
  }

  /**
   * Stop audio for specific section
   */
  function stopAudioForSection(sectionType) {
    if (audioInstances[sectionType]) {
      const audio = audioInstances[sectionType];
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    }
  }

  /**
   * Offload/cleanup a chapter when leaving it
   * Preserves state for when user returns to the chapter
   * @param {string} sectionType - The chapter to offload (e.g., 'chapter1', 'chapter2')
   */
  function offloadChapter(sectionType) {
    // Stop audio for this section
    stopAudioForSection(sectionType);
    
    // Call cleanup callback if chapter instance has one
    if (activeChapterInstances[sectionType] && activeChapterInstances[sectionType].cleanup) {
      try {
        activeChapterInstances[sectionType].cleanup();
      } catch (e) {
        // Cleanup failed
      }
    }
    
    // Remove active instance reference (but keep state in chapterStates)
    delete activeChapterInstances[sectionType];
  }

  /**
   * Register a chapter instance with optional cleanup callback
   * @param {string} sectionType - The chapter type
   * @param {object} instance - Object with optional cleanup() method
   */
  function registerChapterInstance(sectionType, instance) {
    activeChapterInstances[sectionType] = instance;
  }

  /**
   * Check if a chapter is currently active/loaded
   * @param {string} sectionType - The chapter to check
   * @returns {boolean} True if chapter is currently loaded
   */
  function isChapterActive(sectionType) {
    return !!activeChapterInstances[sectionType];
  }

  /**
   * SVG.js Helper Functions
   */
  
  /**
   * Check if SVG.js is available
   */
  function isSvgJsAvailable() {
    return typeof SVG !== 'undefined';
  }

  /**
   * Initialize SVG.js from existing SVG DOM element
   * Returns SVG.js instance or null
   */
  function initSvgJs(svgElement) {
    if (!isSvgJsAvailable()) {
      return null;
    }
    
    if (!svgElement) {
      return null;
    }
    
    // Use SVG.adopt to wrap existing SVG element
    try {
      return SVG.adopt(svgElement);
    } catch (e) {
      return null;
    }
  }

  /**
   * Convert screen coordinates to SVG coordinates using SVG.js
   */
  function screenToSvgJs(svgJsInstance, clientX, clientY) {
    if (!svgJsInstance) {
      return { x: 0, y: 0 };
    }
    
    const rect = svgJsInstance.node.getBoundingClientRect();
    const point = new SVG.Point(clientX - rect.left, clientY - rect.top);
    const svgPoint = point.transform(svgJsInstance.node.getScreenCTM().inverse());
    
    return { x: svgPoint.x, y: svgPoint.y };
  }

  /**
   * Check if a point is within an element's bounds using SVG.js
   */
  function isPointInElement(svgJsElement, x, y) {
    if (!svgJsElement) return false;
    
    const bbox = svgJsElement.bbox();
    return x >= bbox.x && x <= bbox.x + bbox.width &&
           y >= bbox.y && y <= bbox.y + bbox.height;
  }

  /**
   * Touch Event Helpers - Get coordinates from mouse or touch events
   */
  function getEventCoordinates(e) {
    if (e.touches && e.touches.length > 0) {
      // Touch event
      return {
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY
      };
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      // Touch end event
      return {
        clientX: e.changedTouches[0].clientX,
        clientY: e.changedTouches[0].clientY
      };
    } else {
      // Mouse event
      return {
        clientX: e.clientX,
        clientY: e.clientY
      };
    }
  }

  /**
   * Add both mouse and touch event listeners
   */
  function addDragListeners(element, moveHandler, endHandler) {
    // Mouse events
    element.addEventListener('mousemove', moveHandler);
    element.addEventListener('click', endHandler);
    
    // Touch events
    element.addEventListener('touchmove', moveHandler, { passive: false });
    element.addEventListener('touchend', endHandler);
  }

  /**
   * Remove both mouse and touch event listeners
   */
  function removeDragListeners(element, moveHandler, endHandler) {
    // Mouse events
    element.removeEventListener('mousemove', moveHandler);
    element.removeEventListener('click', endHandler);
    
    // Touch events
    element.removeEventListener('touchmove', moveHandler);
    element.removeEventListener('touchend', endHandler);
  }

  /**
   * Load SVG dynamically from path
   */
  function loadSvgDynamically(svgContainer, svgPath) {
    return new Promise(function(resolve, reject) {
      // Check if already loaded (check both data attribute and inline SVG)
      const svgStatus = svgContainer.querySelector('[data-svg-status]');
      if (svgStatus || svgContainer.getAttribute('data-svg-loaded') === 'true') {
        const svg = svgContainer.querySelector('svg');
        if (svg) {
          svgContainer.setAttribute('data-svg-loaded', 'true');
          resolve(svg);
          return;
        }
      }
      
      // Check if path is provided
      if (!svgPath) {
        svgPath = svgContainer.getAttribute('data-svg-path');
      }
      
      if (!svgPath) {
        reject(new Error('No SVG path provided'));
        return;
      }
      
      // Fetch SVG content
      fetch(svgPath)
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Failed to load SVG: ' + response.statusText);
          }
          return response.text();
        })
        .then(function(svgContent) {
          // Insert SVG into container
          svgContainer.innerHTML = svgContent;
          svgContainer.setAttribute('data-svg-loaded', 'true');
          
          // Remove any status script tags
          const statusScript = svgContainer.querySelector('[data-svg-status]');
          if (statusScript) {
            statusScript.remove();
          }
          
          // Get the SVG element
          const svg = svgContainer.querySelector('svg');
          if (svg) {
            // Set preserveAspectRatio if not already set
            if (!svg.getAttribute('preserveAspectRatio')) {
              svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
            }
            resolve(svg);
          } else {
            reject(new Error('SVG element not found in loaded content'));
          }
        })
        .catch(function(error) {
          reject(error);
        });
    });
  }

  /**
   * Wait for SVG to fully load and resize
   */
  function waitForSvgReady(sectionElement) {
    return new Promise(function(resolve) {
      const svgContainer = sectionElement.querySelector('[data-mcmn-container]');
      const svg = svgContainer ? svgContainer.querySelector('svg') : null;
      
      if (!svg) {
        // SVG not loaded yet - try to load it dynamically
        if (svgContainer && svgContainer.getAttribute('data-svg-path')) {
          loadSvgDynamically(svgContainer).then(function(loadedSvg) {
            // Wait for dimensions after loading
            setTimeout(function() {
              waitForSvgReady(sectionElement).then(resolve);
            }, 50);
          }).catch(function(error) {
            resolve(); // Resolve anyway to prevent blocking
          });
          return;
        }
        resolve();
        return;
      }
      
      let attempts = 0;
      const maxAttempts = 20; // Maximum 1 second (20 * 50ms)
      
      // Check if SVG is already loaded and has dimensions
      function checkSvgReady() {
        attempts++;
        const rect = svg.getBoundingClientRect();
        const hasDimensions = rect.width > 0 && rect.height > 0;
        
        if (hasDimensions) {
          // Force a reflow to ensure layout is calculated
          svg.offsetHeight;
          svgContainer.offsetHeight;
          sectionElement.offsetHeight;
          
          // Wait for two animation frames to ensure all resize calculations are complete
          requestAnimationFrame(function() {
            requestAnimationFrame(function() {
              // One more check to ensure dimensions are stable
              const finalRect = svg.getBoundingClientRect();
              if (finalRect.width > 0 && finalRect.height > 0) {
                resolve();
              } else {
                // If dimensions disappeared, wait a bit more
                setTimeout(checkSvgReady, 100);
              }
            });
          });
        } else if (attempts < maxAttempts) {
          // If not ready, check again after a short delay
          setTimeout(checkSvgReady, 50);
        } else {
          // Timeout - resolve anyway to prevent infinite waiting
          resolve();
        }
      }
      
      // For inline SVGs (embedded via Twig), they're already in the DOM
      // but we need to wait for layout calculations
      // Start checking after a brief delay to allow initial layout
      setTimeout(checkSvgReady, 10);
    });
  }

  /**
   * Update icon fill color based on state
   */
  function updateIconFill(button, isActive, areHotspotsComplete) {
    const svg = button.querySelector('svg');
    if (!svg) return;

    // Find the circle or path element that represents the icon background
    // First, try to find a circle with stroke (the outer circle)
    let targetElement = null;
    
    // Look for circles - prefer the one with stroke
    const circles = svg.querySelectorAll('circle');
    if (circles.length > 0) {
      // Find the circle with stroke attribute (the outer circle)
      // Check both direct stroke and inherited stroke from parent group
      targetElement = Array.from(circles).find(circle => {
        const stroke = circle.getAttribute('stroke');
        const parent = circle.parentElement;
        const parentStroke = parent && parent.getAttribute('stroke');
        return (stroke && stroke !== 'none') || (parentStroke && parentStroke !== 'none');
      });
      // If no circle with stroke, use the second circle (usually the outer one)
      if (!targetElement && circles.length > 1) {
        targetElement = circles[1]; // Second circle is usually the outer one
      } else if (!targetElement && circles.length > 0) {
        targetElement = circles[0];
      }
    }
    
    // If no circle found, look for path elements that might be the circle background
    // This handles icons like home-icon.svg where the background is a path in a group
    if (!targetElement) {
      const paths = svg.querySelectorAll('path');
      // Look for circle paths (containing 'A' for arc) with white fill - this is the background
      // Also check for paths that start with circle-like coordinates (M 27, M27, etc.)
      targetElement = Array.from(paths).find(path => {
        const fill = path.getAttribute('fill');
        const d = path.getAttribute('d') || '';
        const id = path.getAttribute('id') || '';
        const parent = path.parentElement;
        const parentId = parent ? parent.getAttribute('id') || '' : '';
        
        // Skip icon paths (like the house icon Path_114708)
        // These are usually in groups with clip-path or have specific IDs
        // Also skip paths that are NOT the background circle
        if (id.includes('Path_114708') || 
            (id.includes('Path_') && !id.includes('117643') && !id.includes('Ellipse'))) {
          return false;
        }
        
        // Skip paths that are inside groups with clip-path (these are icon content, not background)
        if (parent && parent.getAttribute('clip-path')) {
          return false;
        }
        
        // Look for circle paths - check for arc commands or circle-like patterns
        const isCirclePath = d.includes('A') || d.includes('a') || 
                            d.match(/M\s*27\s*[,\s]0/) || 
                            d.match(/M\s*27\s*[,\s]1/) ||
                            (d.includes('M 27') && d.includes('C'));
        
        // Check for white/transparent fill or parent group with fill="none"
        const hasWhiteFill = fill === '#fff' || fill === '#FFFFFF' || fill === 'white' || fill === 'none';
        const parentHasNoneFill = parent && parent.getAttribute('fill') === 'none';
        
        // For home icon specifically, look for Path_117643 group's path (the background circle)
        // This is the circle path inside the Path_117643 group
        if (parentId === 'Path_117643' || id.includes('117643')) {
          // Make sure it's the circle path, not the icon path
          return isCirclePath && !parent.getAttribute('clip-path');
        }
        
        // For other icons, look for white-filled circle paths that are NOT icon content
        return hasWhiteFill && isCirclePath && !parent.getAttribute('clip-path');
      });
    }
    
    // If still not found, look for groups with fill="none" that contain circle paths (like Path_117643)
    if (!targetElement) {
      const groups = svg.querySelectorAll('g[fill="none"]');
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        const id = group.getAttribute('id') || '';
        // Look for background circle groups (like Path_117643)
        // Skip groups with clip-path (these contain icon content, not background)
        if (group.getAttribute('clip-path')) {
          continue;
        }
        
        if (id.includes('117643') || (id.includes('Path_') && !id.includes('114708'))) {
          // Find the circle path inside this group
          const circlePath = group.querySelector('path[d*="A"], path[d*="M 27"], path[d*="M27"], path[d*="M 27"]');
          if (circlePath) {
            // Make sure it's not the icon path (Path_114708)
            const pathId = circlePath.getAttribute('id') || '';
            if (!pathId.includes('114708')) {
              targetElement = circlePath;
              break;
            }
          }
        }
      }
    }
    
    // Final fallback: directly query for Path_117643 group and get its circle path
    if (!targetElement) {
      const path117643Group = svg.querySelector('g[id*="117643"]');
      if (path117643Group) {
        const circlePath = path117643Group.querySelector('path');
        if (circlePath) {
          const pathId = circlePath.getAttribute('id') || '';
          // Make sure it's not Path_114708 (the house icon)
          if (!pathId.includes('114708')) {
            targetElement = circlePath;
          }
        }
      }
    }

    if (targetElement) {
      // Safety check: Never modify icon paths (like Path_114708 - the house icon)
      const targetId = targetElement.getAttribute('id') || '';
      if (targetId.includes('114708')) {
        return;
      }
      
      if (isActive) {
        // Active state - fill with #ab0b47 (red) - takes priority over complete
        targetElement.setAttribute('fill', '#ab0b47');
      } else if (areHotspotsComplete) {
        // Hotspots completed state - fill with #004075 (blue)
        targetElement.setAttribute('fill', '#004075');
      } else {
        // Default state - transparent fill
        if (targetElement.tagName === 'circle') {
          targetElement.setAttribute('fill', 'transparent');
        } else {
          // For paths, set transparent fill
          targetElement.setAttribute('fill', 'transparent');
        }
      }
    }
  }

  /**
   * Exit fullscreen mode
   */
  function exitFullscreen() {
    if (document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.webkitCurrentFullScreenElement ||
        document.mozFullScreenElement || 
        document.msFullscreenElement) {
      
      let exitPromise = null;
      
      if (document.exitFullscreen) {
        exitPromise = document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        exitPromise = document.webkitExitFullscreen();
      } else if (document.webkitCancelFullScreen) {
        // Older Safari (capital S)
        exitPromise = document.webkitCancelFullScreen();
      } else if (document.mozCancelFullScreen) {
        exitPromise = document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        exitPromise = document.msExitFullscreen();
      }
      
      // Handle promise rejection
      if (exitPromise && exitPromise.catch) {
        exitPromise.catch(function(error) {
          // Exit failed
        });
      }
    }
  }

  /**
   * Initialize welcome/completion modal
   */
  function initModal(component) {
    const modalOverlay = component.querySelector('[data-mcmn-modal-overlay]');
    const modalContent = modalOverlay ? modalOverlay.querySelector('.mcmn-modal-content') : null;
    const startButton = component.querySelector('[data-mcmn-modal-start]');
    //const mindtripButton = component.querySelector('[data-mcmn-modal-mindtrip]');
    const modalInitial = component.querySelector('[data-mcmn-modal-state="initial"]');
    const modalCompletion = component.querySelector('[data-mcmn-modal-state="completion"]');
    const modalCloseButton = component.querySelector('.mcmn-modal-close');
    
    if (!modalOverlay) return;
    
    // Check if URL has chapter parameter (direct chapter link) - don't show welcome modal if so
    const urlParams = new URLSearchParams(window.location.search);
    const hasChapterParam = urlParams.has('chapter');
    
    // Check if user has already started/completed the experience using cookies
    const hasStarted = CookieManager.hasStarted();
    const hasCompleted = CookieManager.hasCompleted();
    
    // Show appropriate modal state
    if (hasCompleted) {
      // Show completion modal
      showCompletionModal();
    } else if (hasStarted || hasChapterParam) {
      // Hide modal if already started OR if accessing via direct chapter link
      hideModal();
    } else {
      // Show welcome modal on first visit (no chapter param, not started)
      showWelcomeModal();
    }
    
    function showWelcomeModal() {
      if (modalInitial) modalInitial.style.display = 'block';
      if (modalCompletion) modalCompletion.style.display = 'none';
      modalOverlay.classList.remove('hidden');
      
      // Hide close button on welcome modal
      if (modalCloseButton) {
        modalCloseButton.style.display = 'none';
      }
    }
    
    function showCompletionModal() {
      // Delay the completion modal appearance
      setTimeout(function() {
        if (modalInitial) modalInitial.style.display = 'none';
        if (modalCompletion) modalCompletion.style.display = 'block';
        modalOverlay.classList.remove('hidden');
        
        // Show close button on completion modal
        if (modalCloseButton) {
          modalCloseButton.style.display = 'flex';
        }
        
        // Exit fullscreen mode when showing completion modal
        exitFullscreen();
      }, 1000); // 1 second delay
    }
    
    function hideModal() {
      modalOverlay.classList.add('hidden');
    }
    
    // Start button click handler (mobile touch support)
    if (startButton) {
      const startHandler = function(e) {
        e.preventDefault();
        e.stopPropagation();
        CookieManager.markStarted();
        hideModal();
      };
      startButton.addEventListener('click', startHandler);
      startButton.addEventListener('touchend', startHandler, { passive: false });
    }
    
    // MindTrip button click handler - trigger MindTrip widget and hide modal (mobile touch support)
    // if (mindtripButton) {
    //   const mindtripHandler = function(e) {
    //     e.preventDefault();
    //     e.stopPropagation();
    //     hideModal();
        
    //     // Try to trigger MindTrip widget if available
    //     // The MindTrip embed creates a widget that can be triggered
    //     setTimeout(function() {
    //       // Look for MindTrip button/trigger on the page
    //       const mindtripTrigger = document.querySelector('[data-mindtrip-button], .mindtrip-widget-trigger');
    //       if (mindtripTrigger) {
    //         mindtripTrigger.click();
    //       } else {
    //         // Fallback: scroll to bottom where MindTrip widget usually loads
    //         window.scrollTo({
    //           top: document.body.scrollHeight,
    //           behavior: 'smooth'
    //         });
    //       }
    //     }, 300);
    //   };
    //   mindtripButton.addEventListener('click', mindtripHandler);
    //   mindtripButton.addEventListener('touchend', mindtripHandler, { passive: false });
    // }
    
    // Close button handler - support both click and touch events for mobile
    const closeButton = component.querySelector('[data-mcmn-modal-close]');
    if (closeButton) {
      const handleClose = function(e) {
        e.preventDefault();
        e.stopPropagation();
        hideModal();
      };
      
      // Add both click and touchstart for better mobile support
      closeButton.addEventListener('click', handleClose);
      closeButton.addEventListener('touchend', function(e) {
        e.preventDefault();
        e.stopPropagation();
        handleClose(e);
      });
    }
    
    // Click/touch outside modal to close - support both events for mobile
    if (modalOverlay && modalContent) {
      // // Prevent clicks on content from closing modal
      // modalContent.addEventListener('click', function(e) {
      //   e.stopPropagation();
      // });
      // modalContent.addEventListener('touchend', function(e) {
      //   e.stopPropagation();
      // });
      
      const handleOverlayClose = function(e) {
        // Only close if clicking/touching the overlay itself, not the content
        if (e.target === modalOverlay || e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          hideModal();
        }
      };
      
      // Add both click and touchend for better mobile support
      modalOverlay.addEventListener('click', handleOverlayClose, true);
      modalOverlay.addEventListener('touchend', function(e) {
        // Only close if touching the overlay itself, not the content
        if (e.target === modalOverlay || e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          handleOverlayClose(e);
        }
      }, true);
    }
    
    // ESC key to close
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && !modalOverlay.classList.contains('hidden')) {
        hideModal();
      }
    });
    
    // Prevent Mindtrip forms from submitting normally (let Mindtrip handle it)
    // const mindtripForms = component.querySelectorAll('.mindtrip-form');
    // mindtripForms.forEach(function(form) {
    //   form.addEventListener('submit', function(e) {
    //     e.preventDefault();
    //     // Mindtrip should intercept the button click before this fires
    //     // This is just a safety net to prevent page reload
    //   });
    // });
    
    // Export functions
    Mcmn.showCompletionModal = showCompletionModal;
    Mcmn.hideModal = hideModal;
  }

  /**
   * Check if all chapters have been viewed and show completion modal
   */
  function checkCompletion(viewedSections) {
    const allSections = ['intro', 'chapter1', 'chapter2', 'chapter3', 'chapter4'];
    // Check that all sections are viewed AND all hotspots are completed
    const allViewed = allSections.every(function(section) {
      return viewedSections.has(section) && CookieManager.areAllHotspotsComplete(section);
    });
    
    // Update navigation icons when hotspots are completed
    const component = document.querySelector('.mcmn-experience-component');
    if (component) {
      const navButtons = component.querySelectorAll('[data-mcmn-nav]');
      if (navButtons.length > 0 && Mcmn.updateIconFill) {
        navButtons.forEach(function(button) {
          const sectionName = button.getAttribute('data-mcmn-nav');
          const isActive = button.classList.contains('active');
          const areHotspotsComplete = CookieManager.areAllHotspotsComplete(sectionName);
          Mcmn.updateIconFill(button, isActive, areHotspotsComplete);
        });
      }
    }
    
    if (allViewed) {
      const hasCompleted = CookieManager.hasCompleted();
      if (!hasCompleted) {
        // Mark as completed in cookie
        CookieManager.markCompleted();
        
        // Add body class for gated content access
        document.body.classList.add('mcmn-experience-completed');
        
        // Update gated content visibility
        if (Mcmn.updateGatedContentVisibility) {
          Mcmn.updateGatedContentVisibility();
        }
        
        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
        
        // Wait a moment before showing the completion modal
        setTimeout(function() {
          if (Mcmn.showCompletionModal) {
            Mcmn.showCompletionModal();
          }
        }, 2500);
      }
    }
  }

  /**
   * Initialize section navigation with transitions
   */
  function initSectionNavigation(component) {
    const navButtons = component.querySelectorAll('[data-mcmn-nav]');
    const sections = component.querySelectorAll('[data-mcmn-section]');
    let isTransitioning = false;
    
    // Initialize viewedSections from cookie data (restore previous progress)
    const savedViewedSections = CookieManager.getViewedSections();
    const viewedSections = new Set(savedViewedSections);

    // Function to get section from URL parameter
    function getSectionFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const chapter = urlParams.get('chapter');
      
      if (!chapter || chapter === 'intro') {
        return 'intro';
      }
      
      // Validate that the parameter corresponds to a valid section
      const validSections = ['intro', 'chapter1', 'chapter2', 'chapter3', 'chapter4'];
      if (validSections.includes(chapter)) {
        return chapter;
      }
      return 'intro'; // Default to intro if invalid parameter
    }

    // Get initial section from URL parameter
    const initialSection = getSectionFromUrl();
    viewedSections.add(initialSection);
    
    // Save initial section to cookie
    CookieManager.addViewedSection(initialSection);
    
    // Check for completion after initial section is set
    checkCompletion(viewedSections);

    // Hide all sections first to prevent flash
    sections.forEach(function(section) {
      section.classList.remove('active');
      gsap.set(section, { opacity: 0 });
    });

    // Initial fade-in for the initial section (from hash or default intro)
    const initialSectionElement = component.querySelector('[data-mcmn-section="' + initialSection + '"]');
    if (initialSectionElement) {
      // Set initial active section data attribute for scrollbar styling
      component.setAttribute('data-active-section', initialSection);
      
      // Update nav button state
      navButtons.forEach(function(btn) {
        if (btn.getAttribute('data-mcmn-nav') === initialSection) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
      
      // Show initial section immediately (no delay, no flash)
      initialSectionElement.classList.add('active');
      // Force reflow to ensure display: block is applied
      initialSectionElement.offsetHeight;
      
      // Wait for SVG to fully load and resize before showing
      waitForSvgReady(initialSectionElement).then(function() {
        // Fade in immediately
        gsap.to(initialSectionElement, {
          opacity: 1,
          duration: 1,
          ease: 'power2.out',
          onComplete: function() {
            // Initialize experience for the initial section if it's not intro
            // (intro gets initialized by default in the main initialization)
            if (initialSection !== 'intro' && Mcmn.initMcmnExperience) {
              Mcmn.initMcmnExperience(component);
            }
          }
        });
      });
    }

    // Update all nav button icons based on initial state
    function updateAllNavIcons() {
      navButtons.forEach(button => {
        const sectionName = button.getAttribute('data-mcmn-nav');
        const isActive = button.classList.contains('active');
        const areHotspotsComplete = CookieManager.areAllHotspotsComplete(sectionName);
        updateIconFill(button, isActive, areHotspotsComplete);
      });
    }

    // Initial update
    updateAllNavIcons();

    // Function to navigate to a section
    function navigateToSection(targetSection, skipHashUpdate) {
      if (isTransitioning) return;
      
      const currentActiveSection = component.querySelector('.mcmn-section.active');
      const targetSectionElement = component.querySelector('[data-mcmn-section="' + targetSection + '"]');

      if (!targetSectionElement || currentActiveSection === targetSectionElement) {
        return;
      }

      isTransitioning = true;

      // Mark target section as viewed (but only count toward completion if hotspots are done)
      viewedSections.add(targetSection);
      
      // Save viewed section to cookie
      CookieManager.addViewedSection(targetSection);
      
      // Check if all sections have been viewed and hotspots completed
      checkCompletion(viewedSections);

      // Offload current chapter before switching
      const currentSection = component.getAttribute('data-active-section');
      if (currentSection && Mcmn.offloadChapter) {
        Mcmn.offloadChapter(currentSection);
      }
      
      // Stop all audio before switching sections (backup)
      if (Mcmn.stopAllAudio) {
        Mcmn.stopAllAudio();
      }

      // Update URL parameter (unless we're navigating because of a URL change)
      if (!skipHashUpdate) {
        const url = new URL(window.location);
        if (targetSection === 'intro') {
          // For intro, remove the chapter parameter
          url.searchParams.delete('chapter');
        } else {
          // Set the chapter parameter
          url.searchParams.set('chapter', targetSection);
        }
        // Use replaceState to avoid adding to browser history and prevent scroll
        history.pushState(null, '', url);
      }

      // Update active section data attribute for scrollbar styling
      component.setAttribute('data-active-section', targetSection);

      // Update active nav button
      navButtons.forEach(btn => {
        if (btn.getAttribute('data-mcmn-nav') === targetSection) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });

      // Update all icons
      updateAllNavIcons();

      // Add transition classes for pseudo-element overlay
      currentActiveSection.classList.add('transitioning-out');
      targetSectionElement.classList.add('transitioning-in');
      
      // Fade out current section
      gsap.to(currentActiveSection, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: function() {
          // Remove active and transition classes from current section
          currentActiveSection.classList.remove('active', 'transitioning-out');
          gsap.set(currentActiveSection, { opacity: 0 }); // Reset to 0 for next time

          // Kill any existing animations on target section
          gsap.killTweensOf(targetSectionElement);
          
          // Show target section but keep it invisible (set display first so SVG can load)
          targetSectionElement.classList.add('active');
          // Set opacity to 0 immediately to prevent flash
          gsap.set(targetSectionElement, { opacity: 0 });
          // Force reflow to ensure display is applied
          targetSectionElement.offsetHeight;
          
          // Load SVG if needed, then wait for it to be ready
          const targetSvgContainer = targetSectionElement.querySelector('[data-mcmn-container]');
          const targetSvgPath = targetSvgContainer ? targetSvgContainer.getAttribute('data-svg-path') : null;
          const targetSvgLoaded = targetSvgContainer ? targetSvgContainer.getAttribute('data-svg-loaded') === 'true' : false;
          
          // Load SVG if not already loaded
          if (targetSvgPath && !targetSvgLoaded) {
            loadSvgDynamically(targetSvgContainer, targetSvgPath).then(function() {
              // Wait for SVG to fully load and resize before showing
              waitForSvgReady(targetSectionElement).then(function() {
                // Ensure opacity is still 0 before fading in
                gsap.set(targetSectionElement, { opacity: 0 });
                
                // Fade in the section
                gsap.to(targetSectionElement, {
                  opacity: 1,
                  duration: 0.3,
                  ease: 'power2.out',
                  onComplete: function() {
                    // Remove transition class
                    targetSectionElement.classList.remove('transitioning-in');
                    isTransitioning = false;
                    // Reinitialize experience for the new section
                    if (Mcmn.initMcmnExperience) {
                      Mcmn.initMcmnExperience(component);
                    }
                  }
                });
              });
            }).catch(function(error) {
              // Still fade in even if SVG load failed
              gsap.set(targetSectionElement, { opacity: 0 });
              gsap.to(targetSectionElement, {
                opacity: 1,
                duration: 0.3,
                ease: 'power2.out',
                onComplete: function() {
                  targetSectionElement.classList.remove('transitioning-in');
                  isTransitioning = false;
                }
              });
            });
          } else {
            // SVG already loaded or no path - proceed normally
            waitForSvgReady(targetSectionElement).then(function() {
              // Ensure opacity is still 0 before fading in
              gsap.set(targetSectionElement, { opacity: 0 });
              
              // Fade in the section
              gsap.to(targetSectionElement, {
                opacity: 1,
                duration: 0.3,
                ease: 'power2.out',
                onComplete: function() {
                  // Remove transition class
                  targetSectionElement.classList.remove('transitioning-in');
                  isTransitioning = false;
                  // Reinitialize experience for the new section
                  if (Mcmn.initMcmnExperience) {
                    Mcmn.initMcmnExperience(component);
                  }
                }
              });
            });
          }
        }
      });
    }

    // Add click handlers to navigation buttons (mobile touch support)
    navButtons.forEach(button => {
      const navHandler = function(e) {
        e.preventDefault();
        e.stopPropagation();
        const targetSection = button.getAttribute('data-mcmn-nav');
        navigateToSection(targetSection, false);
      };
      button.addEventListener('click', navHandler);
      button.addEventListener('touchend', navHandler, { passive: false });
    });

    // Listen for URL changes (browser back/forward buttons)
    window.addEventListener('popstate', function() {
      const newSection = getSectionFromUrl();
      navigateToSection(newSection, true); // Skip URL update since we're responding to a URL change
    });
  }

  /**
   * Initialize fullscreen button
   */
  function initFullscreenButton() {
    const fullscreenButton = document.querySelector('[data-mcmn-fullscreen]');
    if (!fullscreenButton) return;
    
    const experienceContainer = document.querySelector('.mcmn-experience-component');
    if (!experienceContainer) return;
    
    // Check if device is iOS (iPhone, iPad, iPod)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // iOS doesn't support Fullscreen API for regular content, only for video elements
    if (isIOS) {
      fullscreenButton.style.display = 'none';
      return;
    }
    
    // Check if fullscreen is supported
    const isFullscreenSupported = 
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled;
    
    if (!isFullscreenSupported) {
      fullscreenButton.style.display = 'none';
      return;
    }
    
    // Toggle fullscreen function
    function toggleFullscreen() {
      const isCurrentlyFullscreen = 
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.webkitCurrentFullScreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      
      if (!isCurrentlyFullscreen) {
        // Enter fullscreen - handle all browser variations
        let fullscreenPromise = null;
        
        if (experienceContainer.requestFullscreen) {
          fullscreenPromise = experienceContainer.requestFullscreen();
        } else if (experienceContainer.webkitRequestFullscreen) {
          // Modern Safari
          fullscreenPromise = experienceContainer.webkitRequestFullscreen();
        } else if (experienceContainer.webkitRequestFullScreen) {
          // Older Safari (capital S)
          try {
            // Try with keyboard parameter for older Safari
            fullscreenPromise = experienceContainer.webkitRequestFullScreen(Element.ALLOW_KEYBOARD_INPUT);
          } catch (e) {
            fullscreenPromise = experienceContainer.webkitRequestFullScreen();
          }
        } else if (experienceContainer.mozRequestFullScreen) {
          fullscreenPromise = experienceContainer.mozRequestFullScreen();
        } else if (experienceContainer.msRequestFullscreen) {
          fullscreenPromise = experienceContainer.msRequestFullscreen();
        } else if (experienceContainer.webkitEnterFullscreen) {
          // Very old Safari fallback
          experienceContainer.webkitEnterFullscreen();
        }
        
        // Handle promise rejection (user denial, security restrictions, etc.)
        if (fullscreenPromise && fullscreenPromise.catch) {
          fullscreenPromise.catch(function(error) {
            // Don't show error to user, they may have denied it
          });
        }
        
        // Try to lock orientation to landscape on mobile
        if (screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('landscape').catch(function(error) {
            // Orientation lock not supported or failed
          });
        }
      } else {
        // Exit fullscreen - handle all browser variations
        let exitPromise = null;
        
        if (document.exitFullscreen) {
          exitPromise = document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          exitPromise = document.webkitExitFullscreen();
        } else if (document.webkitCancelFullScreen) {
          // Older Safari (capital S)
          exitPromise = document.webkitCancelFullScreen();
        } else if (document.mozCancelFullScreen) {
          exitPromise = document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          exitPromise = document.msExitFullscreen();
        }
        
        // Handle promise rejection
        if (exitPromise && exitPromise.catch) {
          exitPromise.catch(function(error) {
            // Exit failed
          });
        }
        
        // Unlock orientation
        if (screen.orientation && screen.orientation.unlock) {
          screen.orientation.unlock();
        }
      }
    }
    
    // Update button state on fullscreen change
    function updateFullscreenButton() {
      const isFullscreen = 
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.webkitCurrentFullScreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      
      if (isFullscreen) {
        fullscreenButton.classList.add('fullscreen-active');
        experienceContainer.classList.add('is-fullscreen');
      } else {
        fullscreenButton.classList.remove('fullscreen-active');
        experienceContainer.classList.remove('is-fullscreen');
      }
    }
    
    // Click handler (mobile touch support)
    const fullscreenHandler = function(e) {
      e.preventDefault();
      e.stopPropagation();
      toggleFullscreen();
    };
    fullscreenButton.addEventListener('click', fullscreenHandler);
    fullscreenButton.addEventListener('touchend', fullscreenHandler, { passive: false });
    
    // Listen for fullscreen changes - all browser variants
    document.addEventListener('fullscreenchange', updateFullscreenButton);
    document.addEventListener('webkitfullscreenchange', updateFullscreenButton);
    document.addEventListener('webkitbeginfullscreen', updateFullscreenButton); // Older Safari
    document.addEventListener('webkitendfullscreen', updateFullscreenButton); // Older Safari
    document.addEventListener('mozfullscreenchange', updateFullscreenButton);
    document.addEventListener('MSFullscreenChange', updateFullscreenButton);
  }

  /**
   * Handle rotation overlay for mobile devices
   */
  function initRotationOverlay(component) {
    const overlay = component.querySelector('.mcmn-rotate-overlay');
    if (!overlay) return;

    function checkOrientation() {
      const isPortrait = window.innerHeight > window.innerWidth;
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile && isPortrait) {
        overlay.style.display = 'flex';
      } else {
        overlay.style.display = 'none';
      }
    }

    // Check on load
    checkOrientation();

    // Check on resize and orientation change
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', function() {
      setTimeout(checkOrientation, 100);
    });
  }

  /**
   * Initialize MCMN Experience component
   */
  function initMcmnExperience(component) {
    // Check if GSAP is available
    if (typeof gsap === 'undefined') {
      return;
    }
    
    // Check if SVG.js is available
    if (!isSvgJsAvailable()) {
      return;
    }

    // Only initialize the active section
    const activeSection = component.querySelector('.mcmn-section.active');
    if (!activeSection) return;

    const svgContainer = activeSection.querySelector('[data-mcmn-container]');
    
    // Check if SVG needs to be loaded dynamically
    const svgPath = svgContainer ? svgContainer.getAttribute('data-svg-path') : null;
    const svgStatus = svgContainer ? svgContainer.querySelector('[data-svg-status]') : null;
    const isSvgLoaded = svgContainer ? (svgContainer.getAttribute('data-svg-loaded') === 'true' || svgStatus !== null) : false;
    let svg = svgContainer ? svgContainer.querySelector('svg') : null;
    
    // If SVG exists but data-svg-loaded is not set, mark it as loaded
    if (svg && svgContainer && !isSvgLoaded) {
      svgContainer.setAttribute('data-svg-loaded', 'true');
    }
    
    // If SVG not loaded and path is available, load it dynamically
    if (!svg && svgPath && !isSvgLoaded) {
      loadSvgDynamically(svgContainer, svgPath).then(function(loadedSvg) {
        svg = loadedSvg;
        // Set preserveAspectRatio if not already set
        if (!svg.getAttribute('preserveAspectRatio')) {
          svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
        }
        // Initialize the section with the loaded SVG
        initializeSection(component, activeSection, svgContainer, svg);
      }).catch(function(error) {
        // SVG load failed
      });
      return;
    }
    
    if (!svg) {
      return;
    }
    
    // Set preserveAspectRatio if not already set
    if (!svg.getAttribute('preserveAspectRatio')) {
      svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    }
    
    // Initialize the section
    initializeSection(component, activeSection, svgContainer, svg);
  }
  
  /**
   * Initialize a specific section (extracted from initMcmnExperience for reuse)
   */
  function initializeSection(component, activeSection, svgContainer, svg) {

    // SVG sizing (width/height) is handled entirely by CSS
    // preserveAspectRatio is set once above as a one-time attribute initialization

    // Detect which section we're in
    const sectionType = activeSection.getAttribute('data-mcmn-section');

    // Get saved state for this chapter
    const chapterState = getChapterState(sectionType);

    // Initialize Chapter 1 (Tree) interactions
    if (sectionType === 'chapter1') {
      if (Mcmn.initChapter1) {
        Mcmn.initChapter1(svgContainer, svg, chapterState, sectionType);
      }
      return;
    }

    // Initialize Chapter 2 (Parranda) interactions
    if (sectionType === 'chapter2') {
      if (Mcmn.initChapter2) {
        Mcmn.initChapter2(svgContainer, svg, chapterState, sectionType);
      }
      return;
    }

    // Initialize Chapter 3 (Feast) interactions
    if (sectionType === 'chapter3') {
      if (Mcmn.initChapter3) {
        Mcmn.initChapter3(svgContainer, svg, chapterState, sectionType);
      }
      return;
    }

    // Initialize Chapter 4 (Reyes) interactions
    if (sectionType === 'chapter4') {
      if (Mcmn.initChapter4) {
        Mcmn.initChapter4(svgContainer, svg, chapterState, sectionType);
      }
      return;
    }

    // Initialize Intro section interactions
    if (sectionType === 'intro') {
      if (Mcmn.initIntroSection) {
        Mcmn.initIntroSection(svgContainer, svg, activeSection, chapterState, sectionType);
      }
    }
  }

  /**
   * Track newsletter form submission
   * Listens for successful submission of mcmn_experience_popup_newsletter webform
   */
  function initNewsletterTracking() {
    // Listen for Drupal webform AJAX success
    if (typeof Drupal !== 'undefined' && Drupal.ajax) {
      // Hook into webform AJAX success
      document.addEventListener('DOMContentLoaded', function() {
        // Listen for webform submissions
        // Function to check and set newsletter cookie
        function checkAndSetNewsletterCookie() {
          // Check DOM for confirmation - look in multiple places
          const confirmation = document.querySelector('.webform-confirmation') || 
                               document.querySelector('[class*="webform-confirmation"]') ||
                               document.querySelector('.messages--status') ||
                               document.querySelector('[data-drupal-messages] .messages--status');
          
          // Also check if form has been replaced with confirmation
          const newsletterForm = document.querySelector('form.webform-submission-mcmn-experience-popup-newsletter-form, form[data-drupal-selector*="mcmn_experience_popup_newsletter"]');
          const formHasConfirmation = newsletterForm && (
            newsletterForm.querySelector('.webform-confirmation') ||
            newsletterForm.classList.contains('webform-confirmation')
          );
          
          if (confirmation || formHasConfirmation) {
            // Set cookie immediately
            CookieManager.markNewsletterSubmitted();
            
            // Verify cookie was set - check multiple times with delays
            // Sometimes cookies take a moment to be readable
            setTimeout(function() {
              // Add body class for gated content access
              if (CookieManager.hasGatedAccess()) {
                document.body.classList.add('mcmn-gated-access');
              }
              
              // Update gated content visibility immediately
              updateGatedContentVisibility();
              
              // Dispatch custom event for other components to listen to
              document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
            }, 100);
            
            // Also check again after a longer delay to ensure cookie is readable
            setTimeout(function() {
              const hasAccess = CookieManager.hasGatedAccess();
              if (hasAccess) {
                updateGatedContentVisibility();
                document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
              }
            }, 300);
            
            return true;
          }
          return false;
        }
        
        // Check immediately
        checkAndSetNewsletterCookie();
        
        // Listen for Drupal webform AJAX success event (most reliable)
        if (typeof jQuery !== 'undefined') {
          jQuery(document).on('webform:ajax:success', function(event, data) {
            // Check if this is the newsletter form
            const newsletterForm = document.querySelector('form.webform-submission-mcmn-experience-popup-newsletter-form, form[data-drupal-selector*="mcmn_experience_popup_newsletter"]');
            if (newsletterForm) {
              // Set cookie immediately
              if (!CookieManager.hasSubmittedNewsletter()) {
                CookieManager.markNewsletterSubmitted();
              }
              
              // Force update with delays
              setTimeout(function() {
                checkAndSetNewsletterCookie();
                if (CookieManager.hasGatedAccess()) {
                  document.body.classList.add('mcmn-gated-access');
                  updateGatedContentVisibility();
                  document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
                }
              }, 50);
              
              setTimeout(function() {
                checkAndSetNewsletterCookie();
                if (CookieManager.hasGatedAccess()) {
                  document.body.classList.add('mcmn-gated-access');
                  updateGatedContentVisibility();
                  document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
                }
              }, 200);
            }
          });
        }
        
        // Use MutationObserver to watch for confirmation being added to DOM
        const observer = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === 1) { // Element node
                // Check if this node or its children contain confirmation
                if (node.matches && (node.matches('.webform-confirmation') || node.matches('[class*="webform-confirmation"]'))) {
                  checkAndSetNewsletterCookie();
                } else if (node.querySelector && (node.querySelector('.webform-confirmation') || node.querySelector('[class*="webform-confirmation"]'))) {
                  checkAndSetNewsletterCookie();
                }
              }
            });
          });
        });
        
        // Observe the document body for changes
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
        
        // Also check periodically as fallback (more frequently)
        const confirmationCheckInterval = setInterval(function() {
          if (checkAndSetNewsletterCookie()) {
            clearInterval(confirmationCheckInterval);
            observer.disconnect();
          }
          // Also check if cookie was set via submit event
          // NOTE: Newsletter submission now grants immediate access (experience completion not required)
          if (CookieManager.hasSubmittedNewsletter() && !CookieManager.hasGatedAccess()) {
            // Cookie is set but access not yet granted - grant it now
            // ORIGINAL CODE (both required):
            // if (CookieManager.hasCompleted()) {
            //   // Both conditions met - update immediately
            document.body.classList.add('mcmn-gated-access');
            updateGatedContentVisibility();
            document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
            // }
          }
        }, 200); // Check more frequently (every 200ms)
        
        // Stop checking after 10 seconds
        setTimeout(function() {
          clearInterval(confirmationCheckInterval);
          observer.disconnect();
        }, 10000);
        
        // Function to attach form submit listener
        function attachFormSubmitListener() {
          const newsletterForm = document.querySelector('form.webform-submission-mcmn-experience-popup-newsletter-form, form[data-drupal-selector*="mcmn_experience_popup_newsletter"], [data-mcmn-popup] form');
          if (newsletterForm && !newsletterForm.dataset.mcmnSubmitListenerAttached) {
            newsletterForm.dataset.mcmnSubmitListenerAttached = 'true';
            newsletterForm.addEventListener('submit', function(e) {
              // Mark as submitting in sessionStorage for immediate tracking
              sessionStorage.setItem('submitting_newsletter', 'true');
              
              // Set cookie immediately on submit (optimistic update)
              // This ensures the cookie is set even if there's a delay in confirmation
              if (!CookieManager.hasSubmittedNewsletter()) {
                CookieManager.markNewsletterSubmitted();
                
                // Update immediately
                setTimeout(function() {
                  if (CookieManager.hasGatedAccess()) {
                    document.body.classList.add('mcmn-gated-access');
                    updateGatedContentVisibility();
                    document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
                  }
                }, 50);
              }
            });
          }
        }
        
        // Attach listener immediately if form exists
        attachFormSubmitListener();
        
        // Also watch for form being added to DOM (for dynamically loaded popups)
        const formObserver = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === 1) { // Element node
                // Check if this node is a form or contains a form
                if (node.tagName === 'FORM' && (node.getAttribute('data-drupal-selector') && node.getAttribute('data-drupal-selector').indexOf('mcmn_experience_popup_newsletter') > -1)) {
                  attachFormSubmitListener();
                } else if (node.querySelector && node.querySelector('form[data-drupal-selector*="mcmn_experience_popup_newsletter"]')) {
                  attachFormSubmitListener();
                }
              }
            });
          });
        });

        // Observe popup container and document body for form additions
        const popupContainer = document.querySelector('[data-mcmn-popup]');
        if (popupContainer) {
          formObserver.observe(popupContainer, { childList: true, subtree: true });
        }
        formObserver.observe(document.body, { childList: true, subtree: true });
        
        jQuery(document).on('ajaxComplete', function(event, xhr, settings) {
          // Check if this is the newsletter form submission - check both URL and form class
          const isNewsletterForm = (settings.url && settings.url.indexOf('mcmn_experience_popup_newsletter') > -1) ||
                                   (settings.url && settings.url.indexOf('webform') > -1 && document.querySelector('form.webform-submission-mcmn-experience-popup-newsletter-form'));
          
          if (isNewsletterForm) {
            // Set cookie immediately on AJAX complete (don't wait for confirmation)
            // NOTE: Newsletter submission now grants immediate access (experience completion not required)
            if (!CookieManager.hasSubmittedNewsletter()) {
              CookieManager.markNewsletterSubmitted();
            }
            
            // Force multiple updates with delays to catch cookie timing issues
            // Check immediately, then multiple times with increasing delays
            function forceUpdateAccess() {
              const hasAccess = CookieManager.hasGatedAccess();
              // const hasCompleted = CookieManager.hasCompleted(); // DISABLED - not currently required
              const hasNewsletter = CookieManager.hasSubmittedNewsletter();
              
              if (hasAccess) {
                document.body.classList.add('mcmn-gated-access');
                updateGatedContentVisibility();
                document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
                return true;
              } else if (hasNewsletter) {
                // Newsletter submitted - grant access immediately
                // ORIGINAL: else if (hasCompleted && hasNewsletter) - required both
                document.body.classList.add('mcmn-gated-access');
                updateGatedContentVisibility();
                document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
                return true;
              }
              return false;
            }
            
            // Try immediately
            if (!forceUpdateAccess()) {
              // Try again with delays
              setTimeout(forceUpdateAccess, 50);
              setTimeout(forceUpdateAccess, 100);
              setTimeout(forceUpdateAccess, 200);
              setTimeout(forceUpdateAccess, 300);
              setTimeout(forceUpdateAccess, 500);
            }
            
            // Wait a bit for DOM to update, then check for confirmation
            setTimeout(function() {
              checkAndSetNewsletterCookie();
            }, 100);
            
            // Also check response for success indicators
            try {
              const response = xhr.responseJSON;
              if (response && response.length > 0) {
                // Look for confirmation message or success indicator
                const hasSuccess = response.some(function(cmd) {
                  return cmd.command === 'insert' || 
                         cmd.command === 'replace' ||
                         cmd.command === 'update_build_id' ||
                         (cmd.data && typeof cmd.data === 'string' && (
                           cmd.data.indexOf('confirmation') > -1 ||
                           cmd.data.indexOf('thank') > -1 ||
                           cmd.data.indexOf('webform-confirmation') > -1 ||
                           cmd.data.indexOf('success') > -1
                         ));
                });
                
                if (hasSuccess) {
                  // Cookie should already be set, just update UI
                  setTimeout(function() {
                    if (CookieManager.hasGatedAccess()) {
                      document.body.classList.add('mcmn-gated-access');
                      updateGatedContentVisibility();
                      document.dispatchEvent(new CustomEvent('mcmn-gated-access-updated'));
                    }
                  }, 50);
                }
              }
            } catch (e) {
              // Silently handle errors
            }
          }
        });
      });
    }
    
    // Also listen for non-AJAX webform submissions (fallback)
    window.addEventListener('load', function() {
      // Check URL for webform confirmation
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('webform_confirmation') || 
          window.location.pathname.indexOf('confirmation') > -1) {
        // Check if we came from newsletter form
        if (document.referrer.indexOf('mcmn_experience_popup_newsletter') > -1 ||
            sessionStorage.getItem('submitting_newsletter') === 'true') {
          CookieManager.markNewsletterSubmitted();
          sessionStorage.removeItem('submitting_newsletter');
          
          // Add body class for gated content access
          if (CookieManager.hasGatedAccess()) {
            document.body.classList.add('mcmn-gated-access');
          }
        }
      }
    });
  }

  /**
   * Update visibility of gated content elements
   * NOTE: Only checking newsletter submission now - experience completion requirement disabled
   */
  function updateGatedContentVisibility() {
    const hasAccess = CookieManager.hasGatedAccess();
    // const hasCompleted = CookieManager.hasCompleted(); // DISABLED - not currently required
    const hasNewsletter = CookieManager.hasSubmittedNewsletter();
    
    // Update gated content container visibility
    const gatedContentContainers = document.querySelectorAll('.mcmn-book-gated-content');
    gatedContentContainers.forEach(function(container) {
      if (hasAccess) {
        // Full access - ensure container is visible
        container.style.display = '';
        container.style.visibility = '';
        container.style.opacity = '';
        container.classList.remove('gated', 'disabled', 'hidden');
      } else {
        // Gated - container should still be visible, just buttons are gated
        // Don't hide the container, just mark it as gated
        container.classList.add('gated');
      }
    });
    
    // Update all gated buttons - include both data attribute and class selectors
    const gatedButtons = document.querySelectorAll('[data-mcmn-gated], .mcmn-book-button');
    gatedButtons.forEach(function(button) {
      if (hasAccess) {
        // Full access - enable button
        button.classList.remove('gated', 'disabled');
        button.removeAttribute('disabled');
        button.setAttribute('data-gate-reason', '');
      } else {
        // Show disabled state - newsletter required
        button.classList.add('gated', 'disabled');
        button.setAttribute('data-gate-reason', 'submit-newsletter');
        
        // ORIGINAL CODE (both requirements):
        // if (!hasCompleted) {
        //   button.classList.add('disabled');
        //   button.setAttribute('data-gate-reason', 'complete-experience');
        // } else if (!hasNewsletter) {
        //   button.classList.add('disabled');
        //   button.setAttribute('data-gate-reason', 'submit-newsletter');
        // }
      }
    });
    
    // Update gate messages
    const gateMessages = document.querySelectorAll('[data-mcmn-gate-message], .mcmn-gate-message');
    gateMessages.forEach(function(message) {
      if (hasAccess) {
        message.style.display = 'none';
      } else {
        message.style.display = 'block';
      }
    });
  }

  // Initialize newsletter tracking on load
  if (typeof jQuery !== 'undefined') {
    initNewsletterTracking();
  }

  // Export functions to shared namespace
  Mcmn.getChapterState = getChapterState;
  Mcmn.saveChapterState = saveChapterState;
  Mcmn.registerAudio = registerAudio;
  Mcmn.stopAllAudio = stopAllAudio;
  Mcmn.stopAudioForSection = stopAudioForSection;
  Mcmn.offloadChapter = offloadChapter;
  Mcmn.registerChapterInstance = registerChapterInstance;
  Mcmn.isChapterActive = isChapterActive;
  Mcmn.isSvgJsAvailable = isSvgJsAvailable;
  Mcmn.initSvgJs = initSvgJs;
  Mcmn.screenToSvgJs = screenToSvgJs;
  Mcmn.isPointInElement = isPointInElement;
  Mcmn.getEventCoordinates = getEventCoordinates;
  Mcmn.addDragListeners = addDragListeners;
  Mcmn.removeDragListeners = removeDragListeners;
  Mcmn.waitForSvgReady = waitForSvgReady;
  Mcmn.loadSvgDynamically = loadSvgDynamically;
  Mcmn.updateIconFill = updateIconFill;
  Mcmn.initModal = initModal;
  Mcmn.initSectionNavigation = initSectionNavigation;
  Mcmn.initFullscreenButton = initFullscreenButton;
  Mcmn.initRotationOverlay = initRotationOverlay;
  Mcmn.initMcmnExperience = initMcmnExperience;
  Mcmn.updateGatedContentVisibility = updateGatedContentVisibility;

})(Drupal, once);